import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useAuth } from "../context/EnterpriseAuthContext";

/**
 * Full-area loading state used by the route guards (fills the shell's main area, or the
 * viewport outside the shell).
 * @param {{label?: string}} props - Props.
 * @returns {JSX.Element} Spinner.
 */
export function RouteLoading({ label = "Loading..." }) {
  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "var(--q-main-height, 100vh)",
        gap: 2,
      }}
    >
      <CircularProgress size={40} />
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

/**
 * Requires a signed-in user. Unauthenticated visitors go to /login, which returns them to the
 * page they asked for (path + query + hash) after signing in.
 * @param {{children: React.ReactNode}} props - Props.
 * @returns {JSX.Element} Children, a redirect, or a loading state.
 */
function ProtectedRoute({ children }) {
  const { state } = useAuth();
  const location = useLocation();

  if (state.loading) return <RouteLoading />;

  if (state.requiresPasswordReset && location.pathname !== "/reset-password") {
    return <Navigate to="/reset-password" replace />;
  }

  if (!state.isAuthenticated) {
    return (
      <Navigate
        to="/login"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
        replace
      />
    );
  }

  return children;
}

export default ProtectedRoute;
